import React, { useEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import type { SectionComponentProps } from "../../registry.js";

gsap.registerPlugin(ScrollTrigger);

interface HeroFullBleedProps {
  logoUrl?: string;
  logoAlt?: string;
  headlineLogoUrl?: string;
  headlineLogoAlt?: string;
  eyebrow?: string;
  headline?: string;
  accentPhrase?: string;
  subheadline?: string;
  ctaPrimaryLabel?: string;
  ctaPrimaryTarget?: string;
  ctaSecondaryLabel?: string;
  ctaSecondaryTarget?: string;
  imageUrl?: string;
  imageAlt?: string;
  /** "left" (default) or "center" — aligns the entire content block */
  contentAlign?: "left" | "center";
}

function WordClip({ text, className }: { text: string; className?: string }) {
  const words = text.split(" ");
  return (
    <span className={className} aria-label={text}>
      {words.map((w, i) => (
        <React.Fragment key={i}>
          <span className="inline-block overflow-hidden leading-[1.1] align-bottom">
            <span className="hfb-word inline-block">{w}</span>
          </span>
          {i < words.length - 1 && " "}
        </React.Fragment>
      ))}
    </span>
  );
}

function scrollToHash(target: string) {
  const id = target.startsWith("#") ? target.slice(1) : target;
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY;
  window.scrollTo({ top, behavior: "smooth" });
}

export function HeroFullBleed({ props }: SectionComponentProps) {
  const {
    logoUrl,
    logoAlt,
    headlineLogoUrl,
    headlineLogoAlt,
    eyebrow = "",
    headline = "Mobile Detailing",
    accentPhrase = "That Comes To You",
    subheadline = "No waiting rooms, no drop-off. We bring industrial-grade equipment and premium products straight to your driveway.",
    ctaPrimaryLabel = "Book Your Detail",
    ctaPrimaryTarget = "#services",
    ctaSecondaryLabel = "Our Story",
    ctaSecondaryTarget = "#story",
    imageUrl = "",
    imageAlt = "",
    contentAlign = "left",
  } = props as HeroFullBleedProps;

  const isCenter = contentAlign === "center";

  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

      tl.fromTo(".hfb-image-wrap", { scale: 1.12, opacity: 0 }, { scale: 1.04, opacity: 1, duration: 2.2, ease: "power2.out" }, 0)
        .fromTo(".hfb-eyebrow", { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 0.55 }, 0.3)
        .fromTo(".hfb-word", { yPercent: 115, opacity: 0 }, { yPercent: 0, opacity: 1, duration: 0.85, stagger: 0.06 }, 0.55)
        .fromTo(".hfb-sub", { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 0.65 }, 1.05)
        .fromTo(".hfb-cta", { opacity: 0, y: 14 }, { opacity: 1, y: 0, duration: 0.5, stagger: 0.1 }, 1.3)
        .fromTo(".hfb-scroll-hint", { opacity: 0 }, { opacity: 1, duration: 0.5 }, 1.8);

      gsap.to(".hfb-image-wrap", {
        yPercent: 18,
        ease: "none",
        scrollTrigger: { trigger: el, start: "top top", end: "bottom top", scrub: 1.8 },
      });

      gsap.to(".hfb-content", {
        opacity: 0,
        y: -48,
        ease: "none",
        scrollTrigger: { trigger: el, start: "30% top", end: "80% top", scrub: 1.2 },
      });

      gsap.to(".hfb-scroll-arrow", {
        y: 8, repeat: -1, yoyo: true, duration: 0.9, ease: "power1.inOut", delay: 2.2,
      });
    }, el);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center overflow-hidden bg-background"
    >
      {/* Background image with parallax wrapper */}
      <div className="hfb-image-wrap absolute inset-0 z-0 scale-110">
        {imageUrl ? (
          <img src={imageUrl} alt={imageAlt} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-background" />
        )}
        {/* Directional gradient — always dark so text stays legible against the photo */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/60 to-transparent" />
        {/* Full-screen dark veil */}
        <div className="absolute inset-0 bg-black/35" />
        {/* Bottom vignette into section bg */}
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-background to-transparent" />
      </div>

      {/* Content */}
      <div className="hfb-content relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full pt-24 pb-32">
        <div className={`max-w-3xl${isCenter ? " mx-auto text-center" : ""}`}>
          {logoUrl && (
            <img
              src={logoUrl}
              alt={logoAlt ?? ""}
              className={`hfb-eyebrow block mb-1 h-48 w-auto max-w-sm object-contain${isCenter ? " mx-auto" : ""}`}
            />
          )}
          {eyebrow && (
            <span className="hfb-eyebrow block text-primary text-xs font-bold uppercase tracking-[0.32em] mb-5 font-display">
              {eyebrow}
            </span>
          )}

          {headlineLogoUrl ? (
            <img
              src={headlineLogoUrl}
              alt={headlineLogoAlt ?? [headline, accentPhrase].filter(Boolean).join(" ")}
              className={`hfb-word block w-auto max-w-lg object-contain mb-8${isCenter ? " mx-auto" : ""}`}
            />
          ) : (
            /* Text over a dark photo — always white for legibility */
            <h1 className="text-6xl md:text-8xl font-bold leading-[1.0] text-white mb-8 font-display uppercase">
              <WordClip text={headline} />
              {accentPhrase && (
                <>
                  {" "}
                  <span className="relative inline-block">
                    <WordClip text={accentPhrase} />
                    <span className="absolute -bottom-1 left-0 w-full h-[8px] bg-primary -z-10 opacity-85" aria-hidden />
                  </span>
                </>
              )}
            </h1>
          )}

          {subheadline && (
            <p className="hfb-sub text-xl text-white/65 max-w-xl leading-relaxed mb-10">
              {subheadline}
            </p>
          )}

          <div className={`flex flex-col sm:flex-row gap-4${isCenter ? " justify-center" : ""}`}>
            {ctaPrimaryLabel && (
              <a
                href={ctaPrimaryTarget}
                onClick={(e) => { if (ctaPrimaryTarget?.startsWith("#")) { e.preventDefault(); scrollToHash(ctaPrimaryTarget); } }}
                className="hfb-cta inline-flex items-center justify-center bg-primary text-primary-foreground px-10 py-4 text-sm font-bold uppercase tracking-widest hover:opacity-90 transition-opacity font-display"
                style={{ borderRadius: "var(--radius, 2px)" }}
              >
                {ctaPrimaryLabel}
              </a>
            )}
            {ctaSecondaryLabel && (
              <a
                href={ctaSecondaryTarget}
                onClick={(e) => { if (ctaSecondaryTarget?.startsWith("#")) { e.preventDefault(); scrollToHash(ctaSecondaryTarget); } }}
                className="hfb-cta inline-flex items-center justify-center border-2 border-white/30 text-white px-10 py-4 text-sm font-bold uppercase tracking-widest hover:bg-white/10 transition-colors font-display"
                style={{ borderRadius: "var(--radius, 2px)" }}
              >
                {ctaSecondaryLabel}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Scroll hint */}
      <div className="hfb-scroll-hint absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-[0.24em] text-white/40">Scroll</span>
        <svg className="hfb-scroll-arrow w-5 h-5 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </section>
  );
}
